// src/components/mdx/client/Accordion.client.tsx

/**
 * Accordion Component
 * =================
 * 
 * This file provides an accessible, customizable accordion component for displaying
 * collapsible content sections. It supports multiple variants, sizes, and animation options.
 * 
 * Key Features:
 * - Multiple visual variants (default, bordered, minimal)
 * - Configurable sizes (sm, md, lg)
 * - Optional icons and badges for accordion items
 * - Support for single or multiple open sections
 * - Smooth animations with Framer Motion
 * - Fully accessible with proper ARIA attributes
 * - Built-in error handling for icons with ErrorBoundary
 * 
 * @module components/mdx/client/Accordion.client
 */

'use client'

import { useState, type ReactNode, Children, isValidElement, type ReactElement } from 'react'
import { motion, AnimatePresence } from "motion/react"
import { ChevronDown } from 'lucide-react'
import { cn } from '~/lib/utils'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'

/**
 * Props for individual accordion items
 * 
 * @interface AccordionItemProps
 * @typedef {Object} AccordionItemProps
 * @property {string} title - The title displayed in the accordion header
 * @property {ReactNode} content - The content to display when the accordion is expanded
 * @property {boolean} [defaultOpen] - Whether this item should be open by default
 * @property {ReactNode} [icon] - Optional icon to display next to the title
 * @property {string | number} [badge] - Optional badge text or number to display
 */
export interface AccordionItemProps {
  /** The title displayed in the accordion header */
  title: string
  /** The content to display when the accordion is expanded */
  content: ReactNode
  /** Whether this item should be open by default */
  defaultOpen?: boolean
  /** Optional icon to display next to the title */
  icon?: ReactNode
  /** Optional badge text or number to display */
  badge?: string | number
}

/**
 * Props for the Accordion component - supports both props-based and children-based APIs
 * 
 * @interface AccordionProps
 * @typedef {Object} AccordionProps
 * @property {AccordionItemProps[]} [items] - Array of accordion items to display (props-based API)
 * @property {ReactNode} [children] - ReactNode children - expects <details> elements (children-based API)
 * @property {string} [className] - Optional additional CSS classes
 * @property {'default' | 'bordered' | 'minimal'} [variant='default'] - Visual style variant
 * @property {'sm' | 'md' | 'lg'} [size='md'] - Size variant
 * @property {boolean} [allowMultiple=false] - Whether multiple items can be open simultaneously
 * @property {boolean} [animate=true] - Whether to animate transitions
 * @property {string} [title] - Optional title for the accordion section
 * @property {string} [description] - Optional description for the accordion section
 */
export interface AccordionProps {
  /** Array of accordion items to display (props-based API) */
  items?: AccordionItemProps[]
  /** ReactNode children - expects <details> elements (children-based API) */
  children?: ReactNode
  /** Optional additional CSS classes */
  className?: string
  /** Visual style variant */
  variant?: 'default' | 'bordered' | 'minimal'
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Whether multiple items can be open simultaneously */
  allowMultiple?: boolean
  /** Whether to animate transitions */
  animate?: boolean
  /** Optional title for the accordion section */
  title?: string
  /** Optional description for the accordion section */
  description?: string
}

/**
 * Type guard to check if a ReactElement is a details element
 */
export function isDetailsElement(element: ReactElement): element is ReactElement<React.DetailedHTMLProps<React.DetailsHTMLAttributes<HTMLDetailsElement>, HTMLDetailsElement>> {
  return element.type === 'details'
}

/**
 * Type guard to check if a ReactElement is a summary element
 */
export function isSummaryElement(element: ReactElement): element is ReactElement<React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & { 'data-title'?: string; 'data-badge'?: string | number; 'data-icon'?: ReactNode }> {
  return element.type === 'summary'
}

/**
 * Style variants for the accordion
 */
const variantStyles = {
  default: {
    wrapper: 'bg-white dark:bg-gray-800 rounded-lg shadow-sm',
    item: 'border-b last:border-b-0 border-gray-200 dark:border-gray-700',
    button: 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
  },
  bordered: {
    wrapper: 'border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700',
    item: '',
    button: 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
  },
  minimal: {
    wrapper: '',
    item: 'border-b border-gray-200 dark:border-gray-700',
    button: 'hover:bg-transparent'
  }
} as const

/**
 * Size variants for the accordion
 */
const sizeStyles = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6'
} as const

/**
 * Custom fallback UI for icon rendering errors
 */
const IconErrorFallback = (): JSX.Element => (
  <div className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
    <span className="text-xs text-gray-500 dark:text-gray-400">?</span>
  </div>
)

/**
 * Safely wraps each accordion item's icon with an error boundary
 * to prevent icon rendering errors from breaking the entire component
 */
const wrapItemWithSafeIcon = (item: AccordionItemProps): AccordionItemProps => {
  // If the item doesn't have an icon, return it as is
  if (!item.icon) {
    return item
  }

  // Wrap the icon with an error boundary
  return {
    ...item,
    icon: (
      <ErrorBoundary fallback={<IconErrorFallback />}>
        {item.icon}
      </ErrorBoundary>
    )
  }
}

/**
 * Extract accordion items from children (details elements) - for children-based API
 * This function safely parses React children that are expected to be <details> elements
 * and extracts the necessary props to create AccordionItemProps
 */
function parseAccordionChildren(children: ReactNode): AccordionItemProps[] {
  const items: AccordionItemProps[] = []

  Children.forEach(children, (child) => {
    // Type guard to ensure we're working with a valid ReactElement
    if (!isValidElement(child) || !isDetailsElement(child)) {
      return
    }

    // Extract props from the details element
    const detailsProps = child.props
    const isDefaultOpen = detailsProps.open ?? false
    
    let title = ''
    let content: ReactNode = null
    let badge: string | number | undefined
    let icon: ReactNode | undefined

    // Parse children of the details element to find summary and content
    Children.forEach(detailsProps.children, (detailChild) => {
      if (!isValidElement(detailChild)) {
        return
      }

      if (isSummaryElement(detailChild)) {
        // Extract data from summary element
        const summaryProps = detailChild.props
        title = summaryProps['data-title'] ?? 
               (typeof summaryProps.children === 'string' 
                 ? summaryProps.children 
                 : 'Accordion Item')
        badge = summaryProps['data-badge']
        
        // Extract icon if provided
        if (summaryProps['data-icon']) {
          icon = summaryProps['data-icon']
        }
      } else {
        // Any other element becomes the content
        content = detailChild
      }
    })

    // Add the parsed item to our collection
    items.push({
      title,
      content,
      defaultOpen: isDefaultOpen,
      badge,
      icon
    })
  })

  return items
}

/**
 * Accordion component for displaying collapsible content sections.
 * Supports both props-based and children-based APIs for RSC compatibility.
 * 
 * @function Accordion
 * @param {AccordionProps} props - Component properties
 * @param {AccordionItemProps[]} [props.items] - Array of accordion items to display (props-based API)
 * @param {ReactNode} [props.children] - ReactNode children - expects <details> elements (children-based API)
 * @param {string} [props.className] - Optional additional CSS classes
 * @param {'default' | 'bordered' | 'minimal'} [props.variant='default'] - Visual style variant
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Size variant
 * @param {boolean} [props.allowMultiple=false] - Whether multiple items can be open simultaneously
 * @param {boolean} [props.animate=true] - Whether to animate transitions
 * @param {string} [props.title] - Optional title for the accordion section
 * @param {string} [props.description] - Optional description for the accordion section
 * @returns {JSX.Element} Rendered component
 * @example
 * ```tsx
 * // Props-based API (legacy)
 * <Accordion
 *   title="Frequently Asked Questions"
 *   items={[
 *     {
 *       title: "How do I get started?",
 *       content: <p>Sign up for an account and follow the onboarding guide.</p>,
 *       defaultOpen: true
 *     }
 *   ]}
 *   variant="bordered"
 *   allowMultiple={true}
 * />
 * 
 * // Children-based API (RSC compatible)
 * <Accordion title="FAQ" variant="bordered" allowMultiple={true}>
 *   <details open>
 *     <summary data-title="How do I get started?">How do I get started?</summary>
 *     <div><p>Sign up for an account and follow the onboarding guide.</p></div>
 *   </details>
 * </Accordion>
 * ```
 */
export function Accordion({
  items,
  children,
  className,
  variant = 'default',
  size = 'md',
  allowMultiple = false,
  animate = true,
  title,
  description
}: AccordionProps): JSX.Element {
  // Determine which API to use and parse items accordingly
  let parsedItems: AccordionItemProps[]
  
  if (children) {
    // Children-based API - parse from details elements
    parsedItems = parseAccordionChildren(children)
  } else if (items) {
    // Props-based API - use items directly
    parsedItems = items
  } else {
    // Neither provided - return empty accordion
    parsedItems = []
  }
  
  // Process items to wrap icons with error boundaries
  const safeItems = parsedItems.map(wrapItemWithSafeIcon)
  
  // Initialize state with items that should be open by default
  const [openItems, setOpenItems] = useState<number[]>(
    safeItems.reduce<number[]>((acc, item, index) => {
      if (item.defaultOpen) acc.push(index)
      return acc
    }, [])
  )

  /**
   * Toggle the open/closed state of an accordion item
   * @param index - The index of the item to toggle
   */
  const toggleItem = (index: number): void => {
    if (allowMultiple) {
      // Allow multiple items to be open
      setOpenItems(current =>
        current.includes(index)
          ? current.filter(i => i !== index)
          : [...current, index]
      )
    } else {
      // Only allow one item to be open at a time
      setOpenItems(current =>
        current.includes(index) ? [] : [index]
      )
    }
  }

  /**
   * Check if an accordion item is currently open
   * @param index - The index of the item to check
   * @returns Whether the item is open
   */
  const isOpen = (index: number): boolean => openItems.includes(index)

  /**
   * Handle keyboard navigation for accessibility
   * Supports arrow keys, Home, and End for navigation between accordion items
   */
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    const { key } = event
    const buttons = document.querySelectorAll('[data-accordion-trigger]')
    if (!buttons.length) return

    // Find the currently focused button
    const currentIndex = Array.from(buttons).findIndex(button => 
      button === document.activeElement
    )

    switch (key) {
      case 'ArrowDown': {
        event.preventDefault()
        const nextIndex = (currentIndex + 1) % buttons.length
        const nextButton = buttons[nextIndex] as HTMLButtonElement | undefined
        nextButton?.focus()
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prevIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1
        const prevButton = buttons[prevIndex] as HTMLButtonElement | undefined
        prevButton?.focus()
        break
      }
      case 'Home': {
        event.preventDefault()
        const firstButton = buttons[0] as HTMLButtonElement | undefined
        firstButton?.focus()
        break
      }
      case 'End': {
        event.preventDefault()
        const lastButton = buttons[buttons.length - 1] as HTMLButtonElement | undefined
        lastButton?.focus()
        break
      }
    }
  }

  return (
    <div className="w-full" onKeyDown={handleKeyDown}>
      {/* Optional header section with title and description */}
      {(title ?? description) && (
        <div className="mb-4">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      )}
      
      {/* Main accordion container */}
      <div className={cn(
        'w-full',
        variantStyles[variant].wrapper,
        className
      )}>
        {safeItems.map((item, index) => (
          <div
            key={index}
            className={cn(
              'overflow-hidden',
              variantStyles[variant].item
            )}
          >
            {/* Accordion item header/trigger button */}
            <button
              onClick={() => toggleItem(index)}
              className={cn(
                'flex w-full items-center justify-between',
                'text-left transition-colors duration-200',
                sizeStyles[size],
                variantStyles[variant].button
              )}
              aria-expanded={isOpen(index)}
              aria-controls={`accordion-content-${index}`}
              data-accordion-trigger
              data-accordion-index={index}
            >
              <div className="flex items-center gap-3">
                {/* Optional icon */}
                {item.icon && (
                  <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
                    {item.icon}
                  </div>
                )}
                {/* Item title */}
                <span className="text-base font-medium text-gray-900 dark:text-white">
                  {item.title}
                </span>
                {/* Optional badge */}
                {item.badge && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                    {item.badge}
                  </span>
                )}
              </div>
              {/* Chevron indicator */}
              <ChevronDown
                className={cn(
                  'h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200',
                  isOpen(index) && 'rotate-180'
                )}
              />
            </button>

            {/* Animated content area */}
            <AnimatePresence>
              {isOpen(index) && (
                <motion.div
                  initial={animate ? { height: 0, opacity: 0 } : false}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  id={`accordion-content-${index}`}
                >
                  <div className={cn(
                    'text-gray-600 dark:text-gray-300',
                    'border-t border-gray-200 dark:border-gray-700',
                    sizeStyles[size]
                  )}>
                    {item.content}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Accordion
